/**
 * Telemetry Utility - Structured Logging with CorrelationId
 *
 * Provides consistent event tracking across the application with:
 * - Unique correlationId per user session
 * - Standardized log schema: {op, component, correlationId, duration_ms, success, err_code?}
 * - Type-safe event logging
 */

export interface TelemetryEvent {
  op: string;
  component: string;
  correlationId: string;
  duration_ms?: number;
  success: boolean;
  err_code?: string;
  err_message?: string;
  [key: string]: unknown; // Allow additional metadata
}

/**
 * Generate or retrieve session correlationId
 */
export function getCorrelationId(): string {
  const SESSION_KEY = 'sp_correlation_id';
  let correlationId = sessionStorage.getItem(SESSION_KEY);

  if (!correlationId) {
    correlationId = `sp_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    sessionStorage.setItem(SESSION_KEY, correlationId);
  }

  return correlationId;
}

/**
 * Log a telemetry event with structured schema
 */
export function logEvent(event: Partial<TelemetryEvent>): void {
  const fullEvent: TelemetryEvent = {
    op: event.op || 'unknown_operation',
    component: event.component || 'unknown_component',
    correlationId: event.correlationId || getCorrelationId(),
    success: event.success ?? true,
    ...event,
  };

  // Log to console in development
  if (import.meta.env.DEV) {
    console.log('[Telemetry]', fullEvent);
  }

  // Send to analytics service in production
  if (import.meta.env.PROD && window.gtag) {
    window.gtag('event', fullEvent.op, {
      component: fullEvent.component,
      correlation_id: fullEvent.correlationId,
      success: fullEvent.success,
      ...fullEvent,
    });
  }
}

/**
 * Create a performance timer for measuring operation duration
 */
export function createTimer() {
  const start = performance.now();

  return {
    end: (): number => {
      return Math.round(performance.now() - start);
    },
  };
}

/**
 * Log an error event with standardized error handling
 */
export function logError(
  op: string,
  component: string,
  error: unknown,
  metadata?: Record<string, unknown>
): void {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorCode = error instanceof Error ? error.name : 'UnknownError';

  logEvent({
    op,
    component,
    success: false,
    err_code: errorCode,
    err_message: errorMessage,
    ...metadata,
  });
}

/**
 * Decorator for automatic operation timing
 */
export function withTelemetry<T>(
  op: string,
  component: string,
  fn: () => Promise<T>
): Promise<T> {
  const timer = createTimer();
  const correlationId = getCorrelationId();

  return fn()
    .then((result) => {
      logEvent({
        op,
        component,
        correlationId,
        duration_ms: timer.end(),
        success: true,
      });
      return result;
    })
    .catch((error) => {
      logError(op, component, error, {
        correlationId,
        duration_ms: timer.end(),
      });
      throw error;
    });
}

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag?: (command: string, eventName: string, params: Record<string, unknown>) => void;
  }
}
